import { loadHtmlElement } from './promises';
import { getCookie } from './utils';
let iframe;
let iframePromise;
// Always include math operations
const defaultContext = {
    sin: Math.sin,
    cos: Math.cos,
    pi: Math.PI,
    PI: Math.PI,
    abs: Math.abs,
    round: Math.round,
    ceil: Math.ceil,
    floor: Math.floor,
    pow: Math.pow,
    sqrt: Math.sqrt,
    random: Math.random
};
let iframeIsolationSkipped = false;
// Needs to be string evaluated to get around 'use strict' restriction, because we have to use with-statements.
/** Assign values in try-catch.
 * `context` is something not defined and throws error in strict mode
 * so we need to reassign the value to a hoisted variable then */
const evalMathExpressionFunctionBody = `
    try {
        context.eval = eval;
        context.code = code;
    } catch(e) {
        var context = window.context || {};
        context.eval = eval;
        context.code = code;
    }
    const scopeProxy = new Proxy(context, {
        has(prop) { return !(prop in context) },
        get(target, prop) {
            if (prop in target) {
                return target[prop];
            }
            if (!(typeof prop === 'symbol')) {
                throw new Error(\`Unexpected token '\${prop.toString()}'.\`);
            }
        }
    });
    with (scopeProxy) {
        return eval(code);
    }`;
const evalMathExpression = new Function('code', evalMathExpressionFunctionBody);
export class SafeEval {
    static eval(code, context = {}) {
        if (!iframe) {
            SafeEval.init();
        }
        if (iframeIsolationSkipped) {
            window.context = Object.assign(Object.assign({}, defaultContext), context);
            return evalMathExpression(code);
        }
        // Doesn't exist when iframeIsolationSkipped
        const contentWindow = iframe === null || iframe === void 0 ? void 0 : iframe.contentWindow;
        const iframeEvalMathExpression = (iframe === null || iframe === void 0 ? void 0 : iframe.contentDocument) && (contentWindow === null || contentWindow === void 0 ? void 0 : contentWindow.evalMathExpression);
        if (!iframeEvalMathExpression || !contentWindow) {
            throw new Error('Safe eval iframe is not initiated.');
        }
        contentWindow.context = Object.assign(Object.assign({}, defaultContext), context);
        return iframeEvalMathExpression(code);
    }
    /**
     * @summary
     * Initialize safe eval function for evaluating animation expressions.
     *
     * @description
     * This function will determine whether to use iframe isolation or skip it.
     * Iframe isolation is mainly for safe-guarding access to global state in the Studio client,
     * but it's used whenever it can, even in published ads. Isolation is skipped only when
     * we are in a sandboxed (i.e. `<iframe sandbox>`) environment.
     *
     * Note, an iframe is needed because '({}).constructor === Object' and you can from there access
     * all global state. An iframe creates a copy of all global objects including '({}).constructor'
     * that is different from Studio client's. Thus, safe-guarding us from accessing Studio client's
     * global state.
     *
     * Note, all published creatives will either be wrapped in an iframe or not. When they are
     * wrapped in an iframe, they are safe. Published ads served through sandboxed iframes prohibit
     * us from creating additional iframes. We don't need the iframe or use isolation,
     * since we already are inside an iframe.
     */
    static async init({ inStudioApp, skipIframeIsolation } = {}) {
        if (iframePromise) {
            return iframePromise;
        }
        if (!iframe && typeof window !== 'undefined') {
            iframe = document.createElement('iframe');
            const style = iframe.style;
            style.position = 'absolute';
            style.pointerEvents = 'none';
            style.width = style.height = style.opacity = style.top = style.left = '0';
            iframePromise = loadHtmlElement(iframe).promise;
            document.body.appendChild(iframe);
            await iframePromise;
            const iframeDoc = iframe.contentDocument;
            if (skipIframeIsolation || !iframeDoc) {
                iframeIsolationSkipped = skipIframeIsolation || !iframeDoc;
            }
            else {
                const nonce = inStudioApp ? getCookie('script-nonce') : '';
                try {
                    const evalScript = document.createElement('script');
                    const url = URL.createObjectURL(new Blob([`window.evalMathExpression = ${evalMathExpression.toString()}`]));
                    evalScript.nonce = nonce;
                    evalScript.src = url;
                    const scriptPromise = loadHtmlElement(evalScript).promise;
                    iframeDoc.head.appendChild(evalScript);
                    await scriptPromise;
                    URL.revokeObjectURL(url);
                }
                catch (_a) {
                    // Fallback to old injection which causes document.write warnings
                    iframeDoc.open();
                    iframeDoc.write(`<!DOCTYPE html><html><head><script nonce="${nonce}">window.evalMathExpression = ${evalMathExpression.toString()}</script></head></html>`);
                    iframeDoc.close();
                }
            }
        }
        return iframe;
    }
    static destroy() {
        if (iframe) {
            document.body.removeChild(iframe);
            iframe = undefined;
        }
    }
}
